import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from 'api/ApiClient';
import type { UserProperties } from 'api/types';

const PROPERTIES_QUERY_KEY = ['getProperties'];

export const useUpdateProperties = () => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (newUserProperties: UserProperties) => apiClient.updateProperties(newUserProperties),
    onMutate: async (newUserProperties) => {
      // Snapshot per mutation: a shared one would let a failing call roll back a later call's fields.
      await queryClient.cancelQueries({ queryKey: PROPERTIES_QUERY_KEY });
      const previousUserProperties = queryClient.getQueryData<UserProperties>(PROPERTIES_QUERY_KEY);

      // Callers pass only the properties they are changing, so merge rather than replace.
      queryClient.setQueryData(PROPERTIES_QUERY_KEY, (old: UserProperties | undefined) => ({
        ...old,
        ...newUserProperties,
      }));

      return { previousUserProperties };
    },
    onError: (error, _newUserProperties, context) => {
      queryClient.setQueryData(PROPERTIES_QUERY_KEY, context?.previousUserProperties);
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  return {
    updateProperties: mutate,
  };
};
